import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  VStack,
  Button,
  IconButton,
  useToast,
  useDisclosure,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import OrderApi from "../../api/order";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { EditIcon } from "@chakra-ui/icons";
import EditAccountModal from "components/Modal/EditAccountModal";

function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      const response = await OrderApi.GetDetails(id);
      setOrder(response.data.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  const handleEditClick = (account) => {
    setCurrentAccount(account);
    onEditModalOpen();
  };

  const recheckOrder = async () => {
    try {
      const response = await OrderApi.Recheck(id);
      setOrder(response.data.order); 
      toast({
        title: "Order rechecked successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "Failed to recheck the order.",
        description: error.response?.data?.message || "An error occurred.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    }
  };


  const markOrderAsDelivered = async () => {
    try {
      const response = await OrderApi.MarkAsDelivered(id);
      if (response.data.delivered_at) {
        setOrder((prev) => ({
          ...prev,
          delivered_at: response.data.delivered_at,
          status: { id: 1, description: "سفارش تحویل داده شده است." },
        }));
      }
      toast({
        title: "سفارش تحویل داده شد",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "خطا در ثبت تحویل سفارش",
        description: error.response?.data?.message || "خطایی رخ داده است.",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const copyAllDetailsToClipboard = async () => {
    if (!order) return;

    const icon1 = "❌ ";
    const icon2 = "💡 ";

    const additionalText = `${icon1}به هیچ عنوان ایمیل و رمز و سایر مشخصات اکانت را ویرایش نکنید.\n${icon2}اکانت را وارد guest یا مهمان نکنید.\n`;

    const emoji = "⚠️";

    const uniqueDescriptions = [...new Set(order.items.map((item) => item?.account_status?.description).filter((d) => d))];

    const description =
      uniqueDescriptions.length > 0 ? uniqueDescriptions.map((d) => `${emoji} ${d}`).join("\n") + "\n" : "";

    const tutIcon = "📚 ";
    const loveIcon = "❤️ ";

    const footerText = `${tutIcon}آموزش وارد کردن اکانت\n @ForestGame_Tutorial\n${loveIcon}فارست گیم در خدمت شماست\n @ForestGame_IR`;

    const allDetails = `${additionalText}${description}\n#${order.worder_id}\n${order.items
      .map(
        (item) =>
          `${item.product_name} ${item.account_status.name.replace(/4/, "PS4").replace(/5/, "PS5")} \n` +
          `Email: ${item.email} \n` +
          `Password: ${item.password} \n` +
          `${item.code ? `Code: ${item.code}` : ""}`.trim()
      )
      .join("\n\n")}\n\n\n${footerText}\n\n`;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(allDetails);
        toast({
          title: "متن کپی شد",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
        await markOrderAsDelivered();
      } catch {
        toast({
          title: "خطا در کپی کردن متن",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = allDetails;
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();

      try {
        document.execCommand("copy");
        toast({
          title: "متن کپی شد",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
        await markOrderAsDelivered();
      } catch {
        toast({
          title: "خطا در کپی کردن متن",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        textArea.remove();
      }
    }
  };

  if (!order) {
    return <Text>Loading...</Text>;
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} alignItems="center" width="100%">
      <Card bg={useColorModeValue("white", "gray.800")} width={{ base: "100%", md: "100%" }} mb="6">
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center">
          <HStack spacing={4}>
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              شماره سفارش: {order.worder_id}
            </Text>
            {order.delivered_at ? (
              <Badge colorScheme="green" fontSize="md" p="2" borderRadius="md">
              تحویل در&nbsp;
              {new Date(order.delivered_at).toLocaleDateString("fa-IR").replace(/\//g, "/")}
              &nbsp;,&nbsp;
              {new Date(order.delivered_at).toLocaleTimeString("fa-IR")}
            </Badge>
            ) : (
              <Badge colorScheme="yellow" fontSize="md" p="2" borderRadius="md">
                تحویل داده نشده
              </Badge>
            )}
          </HStack>

          <Spacer />

          {order.status.id !== 1 && (
            <Button size="md" colorScheme="gray" onClick={recheckOrder}>
              چک کردن مجدد
            </Button>
          )}
        </CardHeader>
        <CardBody>
          <Table variant="simple" color={textColor}>
            <Thead>
              <Tr>
                <Th>نام محصول</Th>
                <Th>ایمیل</Th>
                <Th>ظرفیت</Th>
                <Th>پلتفرم</Th>
                <Th>قیمت</Th>
                <Th>وضعیت</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {order.items.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.product_name}</Td>
                  <Td>{item.email}</Td>
                  <Td>{item.account_status.name}</Td>
                  <Td>{item.account_status.platform === 1 ? "PS4" : item.account_status.platform === 2 ? "PS5" : "Unknown"}</Td>
                  <Td>{item.price}</Td>
                  <Td>
                    <Badge colorScheme={item.status?.id === 1 ? "green" : "red"} fontSize="md" p="1">
                      {item.status?.description}
                    </Badge>
                  </Td>
                  <Td>
                    <IconButton size="sm" colorScheme="blue" aria-label="Edit" icon={<EditIcon />} onClick={() => handleEditClick(item)} mr={1} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <Card bg={useColorModeValue("white", "gray.700")} width={{ base: "50%", md: "50%", sm: "80%" }} mt="6" boxShadow="md" borderRadius="lg">
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            {order.status.id !== 1 ? "اکانت نیاز به تغییرات دارد" : "جزئیات"}
          </Text>
          {order.status.id === 1 && (
            <Button size="md" colorScheme="orange" onClick={copyAllDetailsToClipboard}>
              کپی
            </Button>
          )}
        </CardHeader>
        {order.status.id === 1 && (
          <CardBody>
            <VStack spacing={4} align="start" textAlign="left" width="100%" p="5">
              {order.items.map((item, index) => (
                <Box
                  key={index}
                  width="100%"
                  borderBottom="1px"
                  borderColor={useColorModeValue("gray.200", "gray.600")}
                  pb="4"
                  mb="4"
                  style={{ direction: "ltr" }}
                >
                  <Text>{item.product_name}</Text>
                  <Text fontWeight="bold">email:</Text>
                  <Text>{item.email}</Text>
                  <Text fontWeight="bold">password:</Text>
                  <Text>{item.password}</Text>
                  {item.code && (
                    <>
                      <Text fontWeight="bold" mt="1">
                        code:
                      </Text>
                      <Text>{item.code}</Text>
                    </>
                  )}
                </Box>
              ))}
            </VStack>
          </CardBody>
        )}
      </Card>

      {currentAccount && <EditAccountModal isOpen={isEditModalOpen} onClose={onEditModalClose} accountId={currentAccount.account_id} productId={id} />}
    </Flex>
  );
}

export default OrderDetails;
